import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Loader, MapPin, Plus, Pencil, Trash2 } from 'lucide-react';
import { useBuyerAuth } from '@/contexts/BuyerAuthContext';
import { useBuyerStore } from '@/contexts/BuyerStoreContext';
import { supabaseBuyer } from '@/lib/supabaseBuyer';
import {
  fetchCustomerAddresses,
  createCustomerAddress,
  updateCustomerAddress,
  deleteCustomerAddress,
  type CustomerAddress,
} from '@/lib/customerAddressService';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';

const BR_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const addressSchema = z.object({
  label: z.string().min(1, 'Informe um nome pro endereço'),
  zip_code: z.string().min(8, 'CEP inválido'),
  street: z.string().min(1, 'Informe a rua'),
  number: z.string().min(1, 'Informe o número'),
  complement: z.string().optional(),
  neighborhood: z.string().min(1, 'Informe o bairro'),
  city: z.string().min(1, 'Informe a cidade'),
  state: z.string().length(2, 'Selecione o estado'),
  is_default: z.boolean().default(false),
});

type AddressFormValues = z.infer<typeof addressSchema>;

const emptyAddress: AddressFormValues = {
  label: 'Casa',
  zip_code: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  is_default: false,
};

export default function BuyerAddressesPage() {
  const { customer, loading: authLoading } = useBuyerAuth();
  const { store, path, loginPath } = useBuyerStore();
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [orderCountsByZip, setOrderCountsByZip] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CustomerAddress | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressSchema),
    defaultValues: emptyAddress,
  });

  const loadAddresses = async () => {
    if (!customer || !store) return;
    setLoading(true);
    try {
      const [data, { data: orderRows }] = await Promise.all([
        fetchCustomerAddresses(customer.id),
        supabaseBuyer
          .from('orders')
          .select('shipping_zip_code')
          .eq('store_owner_id', store.id)
          .not('shipping_zip_code', 'is', null),
      ]);
      setAddresses(data);
      const counts: Record<string, number> = {};
      (orderRows || []).forEach((o) => {
        if (!o.shipping_zip_code) return;
        counts[o.shipping_zip_code] = (counts[o.shipping_zip_code] || 0) + 1;
      });
      setOrderCountsByZip(counts);
    } catch {
      toast.error('Erro ao carregar endereços');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, store]);

  if (!authLoading && !customer) {
    return <Navigate to={loginPath} state={{ from: path('/enderecos') }} replace />;
  }

  const openAddDialog = () => {
    setEditingAddress(null);
    form.reset(emptyAddress);
    setDialogOpen(true);
  };

  const openEditDialog = (address: CustomerAddress) => {
    setEditingAddress(address);
    form.reset({
      label: address.label,
      zip_code: address.zip_code,
      street: address.street,
      number: address.number,
      complement: address.complement || '',
      neighborhood: address.neighborhood,
      city: address.city,
      state: address.state,
      is_default: address.is_default,
    });
    setDialogOpen(true);
  };

  const onSubmit = async (data: AddressFormValues) => {
    if (!customer) return;
    setIsSaving(true);
    try {
      const payload = { ...data, complement: data.complement || null };
      if (editingAddress) {
        await updateCustomerAddress(customer.id, editingAddress.id, payload);
        toast.success('Endereço atualizado!');
      } else {
        await createCustomerAddress(customer.id, payload);
        toast.success('Endereço adicionado!');
      }
      setDialogOpen(false);
      await loadAddresses();
    } catch {
      toast.error('Erro ao salvar endereço');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (addressId: string) => {
    if (!customer) return;
    try {
      await deleteCustomerAddress(customer.id, addressId);
      toast.success('Endereço removido');
      await loadAddresses();
    } catch {
      toast.error('Erro ao remover endereço');
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl page-title">Meus Endereços</h1>
        <p className="text-sm text-muted-foreground mt-1">Gerencie os endereços usados nas suas entregas</p>
      </div>

      <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Endereços salvos</h2>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={openAddDialog}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  Adicionar
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingAddress ? 'Editar endereço' : 'Novo endereço'}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="label"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome do endereço</FormLabel>
                          <FormControl>
                            <Input placeholder="Casa, Trabalho..." disabled={isSaving} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <FormField
                        control={form.control}
                        name="zip_code"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>CEP</FormLabel>
                            <FormControl>
                              <Input placeholder="00000-000" disabled={isSaving} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="state"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Estado</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value} disabled={isSaving}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="UF" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {BR_STATES.map((uf) => (
                                  <SelectItem key={uf} value={uf}>
                                    {uf}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cidade</FormLabel>
                          <FormControl>
                            <Input disabled={isSaving} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="neighborhood"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bairro</FormLabel>
                          <FormControl>
                            <Input disabled={isSaving} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-2">
                        <FormField
                          control={form.control}
                          name="street"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Rua</FormLabel>
                              <FormControl>
                                <Input disabled={isSaving} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="number"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Número</FormLabel>
                            <FormControl>
                              <Input disabled={isSaving} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="complement"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Complemento (opcional)</FormLabel>
                          <FormControl>
                            <Input disabled={isSaving} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="is_default"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between gap-2 rounded-md border p-3">
                          <Label htmlFor="is_default" className="cursor-pointer">
                            Usar como endereço padrão
                          </Label>
                          <FormControl>
                            <Switch
                              id="is_default"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={isSaving}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <Button type="submit" className="w-full" disabled={isSaving}>
                      {isSaving ? <Loader className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Salvar endereço
                    </Button>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>
          <div>
            {authLoading || loading ? (
              <div className="space-y-3">
                {[0, 1].map((i) => (
                  <div key={i} className="border border-border rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-14 rounded-full" />
                    </div>
                    <Skeleton className="h-3 w-full max-w-xs" />
                    <Skeleton className="h-3 w-2/3 max-w-xs" />
                  </div>
                ))}
              </div>
            ) : addresses.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground border border-dashed border-border rounded-xl">
                <MapPin className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>Você ainda não salvou nenhum endereço.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {addresses.map((address) => {
                  const usageCount = orderCountsByZip[address.zip_code] || 0;
                  return (
                  <div
                    key={address.id}
                    className={`border bg-card rounded-xl p-4 shadow-sm ${address.is_default ? 'border-primary/40' : 'border-border'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <MapPin className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{address.label}</p>
                          {address.is_default && <Badge variant="secondary">Padrão</Badge>}
                          {usageCount > 0 && (
                            <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                              Usado em {usageCount} {usageCount === 1 ? 'pedido' : 'pedidos'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {address.street}, {address.number}
                          {address.complement ? ` - ${address.complement}` : ''}
                          <br />
                          {address.neighborhood}, {address.city} - {address.state}
                          <br />
                          CEP {address.zip_code}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(address)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remover endereço?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Essa ação não pode ser desfeita.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(address.id)}>
                                Remover
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
      </div>
    </div>
  );
}
